"use client";

import { useEffect } from "react";
import { patchConsoleErrorForRum, setRumInstance } from "@/lib/rum";

interface RumInitProps {
  appMonitorId: string;
  region: string;
  appRelease: string;
}

// Initializes CloudWatch RUM on the client.
//
// Uses resource-based policy auth (signing: false) — no Cognito, no AWS
// credentials in the browser. Protection comes from the domain allowlist
// on the app monitor itself.
//
// Server env vars are read in layout.tsx and passed in as props so we
// avoid Next.js's NEXT_PUBLIC_ build-time baking.
//
// After construction, two extra steps make RUM see errors it would
// otherwise miss:
//   1. setRumInstance() registers the instance in a module singleton so
//      app/error.tsx and app/global-error.tsx can report React 19 render
//      errors (which never reach window.onerror).
//   2. patchConsoleErrorForRum() monkey-patches console.error so the
//      error logs we ship from try/catch sites flow into RUM without a
//      refactor.
export default function RumInit({
  appMonitorId,
  region,
  appRelease,
}: RumInitProps) {
  useEffect(() => {
    if (!appMonitorId) return;

    // Guard against double-initialization under dev hot reload.
    const w = window as Window & { __rumInitialized?: boolean };
    if (w.__rumInitialized) return;

    let cancelled = false;

    (async () => {
      try {
        const { AwsRum } = await import("aws-rum-web");
        if (cancelled) return;

        const rum = new AwsRum(appMonitorId, appRelease, region, {
          sessionSampleRate: 1,
          endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
          telemetries: ["errors", "performance", "http"],
          allowCookies: true,
          enableXRay: false,
          signing: false,
        });

        setRumInstance(rum);
        patchConsoleErrorForRum();
        w.__rumInitialized = true;
      } catch (e) {
        // Use the original console.error via a direct reference to avoid
        // any risk of hitting a half-applied monkey-patch on this path.
        console.error("[RumInit] Failed to initialize CloudWatch RUM", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appMonitorId, region, appRelease]);

  return null;
}
